import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { CloudUpload, Close, Delete, CheckCircle } from '@mui/icons-material';
import { gql, useMutation } from '@apollo/client';
import { Search, Download } from '@mui/icons-material';
import { CSVLink } from 'react-csv';

interface BulkUploadDealersDialogProps {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}

interface UploadResponse {
  totalRecords: number;
  successfullyUploaded: number;
  totalFailedRecords: number;
  duplicateEntries: number;
  nonValidEntries: number;
  dealerUploads: Array<{
    dealerId: string;
    dealerName: string;
    dealerCode: string;
    error: string;
  }>;
}

const GET_URL = gql`
  mutation GetPublicExcelUrl($file: Upload!) {
    getPublicExcelUrl(file: $file) {
      url
    }
  }
`;

const BULK_UPLOAD_DEALERS = gql`
  mutation BulkUploadDealer($url: String!) {
    bulkUploadDealer(url: $url) {
      status
      message
      summary {
        totalEntries
        successEntries
        duplicateEntries
        totalFailedEntries
        nonValidEntries
        faultyEntries
      }
    }
  }
`;

const BulkUploadDealersDialog: React.FC<BulkUploadDealersDialogProps> = ({ open, onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadUrl, setUploadUrl] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [showResponse, setShowResponse] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterError, setFilterError] = useState<string>('all');
  const [showSearch, setShowSearch] = useState(false);

  const [getUploadUrl, { loading: uploadingFile }] = useMutation(GET_URL);
  const [uploadDealers, { loading: uploadingDealers }] = useMutation(BULK_UPLOAD_DEALERS);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx')) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Only .xlsx files are allowed.');
      setSnackbarOpen(true);
      return;
    }

    setFile(selectedFile);
    setUploadUrl('');

    try {
      const { data } = await getUploadUrl({ variables: { file: selectedFile } });
      const url = data?.getPublicExcelUrl?.url;
      if (url) {
        setUploadUrl(url);
        setSnackbarSeverity('success');
        setSnackbarMessage('File uploaded successfully. Ready to upload.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('GET_URL Error:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('File upload failed. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!uploadUrl) return;

    try {
      const { data } = await uploadDealers({ variables: { url: uploadUrl } });

      const status = data?.bulkUploadDealer?.status;
      const message = data?.bulkUploadDealer?.message;
      const summary = data?.bulkUploadDealer?.summary;

      if (status === true) {
        setSnackbarSeverity('success');
        setSnackbarMessage(message || 'Upload successful');

        // FIXED: Safe string conversion + updated field names for new mutation
        const response: UploadResponse = {
          totalRecords: summary?.totalEntries || 0,
          successfullyUploaded: summary?.successEntries || 0,
          totalFailedRecords: summary?.totalFailedEntries || 0, 
          duplicateEntries: summary?.duplicateEntries || 0,
          nonValidEntries: summary?.nonValidEntries || 0,
          dealerUploads: (summary?.faultyEntries || []).map((entry: any) => {
            let dealerName = entry.dealerName || entry.dealer_name || '';
            let dealerCode = entry.dealerCode || entry.dealer_code || '';
            let dealerId = entry.dealerId || entry.dealer_id || entry.id || '';

            // Keep your original regex fallback logic (just in case)
            if ((!dealerName || !dealerCode) && typeof entry.error === 'string') {
              const nameMatch = entry.error.match(/Dealer '([^']+)'/);
              const codeMatch = entry.error.match(/code '([^']+)'/);
              if (!dealerName && nameMatch) dealerName = nameMatch[1];
              if (!dealerCode && codeMatch) dealerCode = codeMatch[1];
            }

            return {
              dealerId: String(dealerId),          
              dealerName: String(dealerName),
              dealerCode: String(dealerCode),      
              error: String(entry.error || entry.message || 'Unknown error')
            };
          })
        };

        setUploadResponse(response);
        setShowResponse(true);
        onUploaded();
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(message || 'Upload failed');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Upload Error:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('Upload failed. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleCloseDialog = () => {
    setFile(null);
    setUploadUrl('');
    setSnackbarOpen(false);
    setShowResponse(false);
    setUploadResponse(null);
    setSearchTerm('');
    setFilterError('all');
    setShowSearch(false);
    onClose();
  };

  const handleResponseClose = () => {
    setShowResponse(false);
    setUploadResponse(null);
    handleCloseDialog();
  };

  // FIXED: Bullet-proof filtering
  const filteredData = useMemo(() => {
    if (!uploadResponse) return [];
    let data = uploadResponse.dealerUploads;

    if (filterError !== 'all') {
      data = data.filter(dealer => dealer.error && dealer.error.includes(filterError));
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(dealer => {
        const id = String(dealer.dealerId || '').toLowerCase();
        const name = String(dealer.dealerName || '').toLowerCase();
        const code = String(dealer.dealerCode || '').toLowerCase();   // ← safe now

        return id.includes(term) || name.includes(term) || code.includes(term);
      });
    }
    return data;
  }, [uploadResponse, searchTerm, filterError]);

  const csvData = useMemo(() => {
    if (!uploadResponse) return [];
    return filteredData.map(dealer => ({
      'Dealer Name': dealer.dealerName,
      'Dealer Code': dealer.dealerCode,
      'Error': dealer.error
    }));
  }, [filteredData, uploadResponse]);

  const isUploading = uploadingFile || uploadingDealers;

  return (
    <>
      {/* Main Upload Dialog - unchanged except safe close handler */}
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Bulk Upload Dealers
          <IconButton onClick={handleCloseDialog} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: '#000' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Your original file upload UI (unchanged) */}
          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upload File <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: 2, px: 2, py: 1, background: '#fff', minHeight: 56 }}>
              <input
                id="bulk-upload-dealers-file"
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                key={file ? file.name : 'no-file'}
              />
              <label htmlFor="bulk-upload-dealers-file">
                <Button variant="contained" component="span" sx={{ background: '#000', color: '#fff', textTransform: 'none', mr: 2, minWidth: 120 }}>
                  Choose File
                </Button>
              </label>
              {file ? (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#222', fontSize: 16 }}>{file.name}</Typography>
                    {uploadUrl && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <CheckCircle fontSize="small" sx={{ color: 'green' }} />
                        <Typography variant="body2" color="green">File uploaded successfully</Typography>
                      </Box>
                    )}
                  </Box>
                  <IconButton onClick={() => { setFile(null); setUploadUrl(''); }} sx={{ color: '#E60012' }}>
                    <Delete />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography sx={{ flex: 1, color: '#888', fontSize: 16 }}>No file chosen</Typography>
                  <IconButton disabled><CloudUpload sx={{ color: '#888' }} /></IconButton>
                </>
              )}
            </Box>
          </Box>
          {isUploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseDialog} disabled={isUploading}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!uploadUrl || isUploading} sx={{ backgroundColor: '#E60012' }}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Response Dialog - unchanged UI, now safe */}
      <Dialog open={showResponse} onClose={handleResponseClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ position: 'relative', pr: 8, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          Upload Response
          <IconButton onClick={handleResponseClose} size="small" sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {uploadResponse && (
            <Box>
              <Typography variant="body1" sx={{ mb: 0, mt: 1 }}>
                <strong>Total Records:</strong> {uploadResponse.totalRecords}&nbsp;
                <strong>Successfully Uploaded:</strong> {uploadResponse.successfullyUploaded}&nbsp;
                <strong>Total Failed Records:</strong> {uploadResponse.totalFailedRecords}&nbsp;
                <strong>Duplicate Entries:</strong> {uploadResponse.duplicateEntries}&nbsp;
                <strong>Non-Valid Entries:</strong> {uploadResponse.nonValidEntries}
              </Typography>

              {uploadResponse.dealerUploads.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="h6" sx={{ mt: 2 }}>Dealer Uploads</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <IconButton size="small" title="Search" onClick={() => setShowSearch(!showSearch)}>
                        <Search fontSize="small" />
                      </IconButton>
                      <CSVLink data={csvData} filename="dealer_upload_response.csv">
                        <IconButton size="small" title="Download"><Download fontSize="small" /></IconButton>
                      </CSVLink>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Filter</InputLabel>
                        <Select value={filterError} onChange={(e) => setFilterError(e.target.value as string)} label="Filter">
                          <MenuItem value="all">All Errors</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {showSearch && (
                    <TextField fullWidth placeholder="Search by Name or Code" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 2 }} size="small" />
                  )}

                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#E50019' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Dealer Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Dealer Code</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredData.map((dealer, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{dealer.dealerName}</TableCell>
                            <TableCell>{dealer.dealerCode}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>{dealer.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResponseClose} variant="contained" sx={{ backgroundColor: '#E60012' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BulkUploadDealersDialog;